'use client';

import React, { useEffect, useState } from 'react';
import { SearchInput } from '@/components/ui/search-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { apiSwaggerClientSide } from '@/shared/api';
import { Group } from '@/sharedTypes/Api';
import { CatalogueFilters } from '../views/CatalogueTableView';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

export const getAccessOptions = (t: TFunction) => [
  { value: 'public', label: t('adminCatalogue.access.public') },
  { value: 'restricted', label: t('adminCatalogue.access.restricted') },
  { value: 'hidden', label: t('adminCatalogue.access.hidden') }
];

interface FiltersProps {
  filters: CatalogueFilters;
  onFilterChange: (key: keyof CatalogueFilters, value: string) => void;
}

const Filters = ({ filters, onFilterChange }: FiltersProps) => {
  const { t } = useTranslation();
  const ACCESS_OPTIONS = getAccessOptions(t);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(true);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await apiSwaggerClientSide.admin.groupsList();
        if (response.data?.success && response.data.data) {
          setGroups(response.data.data);
        }
      } catch (error) {
        console.error('Failed to fetch groups:', error);
      } finally {
        setLoadingGroups(false);
      }
    };
    fetchGroups();
  }, []);

  return (
    <div className="flex gap-3 w-full mb-2">
      {/* Search by title */}
      <div className="basis-[30%]">
        <SearchInput
          value={filters.search}
          onChange={(e) => onFilterChange('search', e.target.value)}
          placeholder={t('adminCatalogue.catalogueTable.searchByTitle')}
          className="w-full"
          inputClassName="h-9"
          testId="catalogue-search-title"
        />
      </div>

      {/* Search by author */}
      <div className="basis-[30%]">
        <SearchInput
          value={filters.searchAuthor}
          onChange={(e) => onFilterChange('searchAuthor', e.target.value)}
          placeholder={t('adminCatalogue.catalogueTable.searchByAuthor')}
          className="w-full"
          inputClassName="h-9"
          testId="catalogue-search-author"
        />
      </div>

      {/* Group select */}
      <div className="flex-1">
        <Select
          value={filters.group}
          onValueChange={(value) => onFilterChange('group', value)}
          disabled={loadingGroups}
        >
          <SelectTrigger data-testid="catalogue-filter-group" className="w-full bg-white">
            <SelectValue placeholder={loadingGroups ? t('adminCatalogue.common.loading') : t('adminCatalogue.catalogueTable.selectGroup')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t('adminCatalogue.catalogueTable.allGroups')}
            </SelectItem>
            {groups.map((group) => (
              <SelectItem key={group._id} value={group._id!}>
                {group.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Visibility select */}
      <div className="basis-[15%]">
        <Select
          value={filters.visibility}
          onValueChange={(value) => onFilterChange('visibility', value)}
        >
          <SelectTrigger data-testid="catalogue-filter-visibility" className="w-full bg-white">
            <SelectValue placeholder={t('adminCatalogue.catalogueTable.visibility')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t('adminCatalogue.catalogueTable.all')}
            </SelectItem>
            {ACCESS_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default Filters;
